import { useEffect, useRef, useState } from "react";

/**
 * useFetch: 簡易な async データ取得。再フェッチは deps の変化トリガで自動。
 *  - キャッシュは持たないので画面遷移ごとに再取得される。
 *  - 中規模になったら SWR / React Query に移行を検討。
 */

export interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useFetch<T>(fn: () => Promise<T>, deps: ReadonlyArray<unknown>): FetchState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    setError(null);
    fnRef.current()
      .then((d) => {
        if (canceled) return;
        setData(d);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (canceled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadKey]);

  return { data, loading, error, reload: () => setReloadKey((k) => k + 1) };
}
